// IndexedDB storage service for encrypted recordings
// Provides data loss prevention by storing encrypted audio locally

export interface StoredRecording {
  id: string;
  appointmentId?: string;
  encounterId?: string;
  patientId: string;
  providerId: string;
  encryptedAudio: ArrayBuffer;
  encryptionMetadata: {
    iv: string;
    authTag: string;
    algorithm: string;
    keyId: string; // Reference to key, not the key itself
  };
  recordingMetadata: {
    duration: number;
    size: number;
    mimeType: string;
    recordedAt: Date;
    deviceInfo?: {
      userAgent: string;
      platform: string;
    };
  };
  uploadStatus: 'pending' | 'uploading' | 'completed' | 'failed' | 'pending_verification' | 'needs_attention';
  uploadAttempts: number;
  lastUploadAttempt?: Date;
  errorMessage?: string;
}

// Database configuration
const DB_NAME = 'AmbientListeningRecordings';
const DB_VERSION = 1;
const STORE_NAME = 'recordings';

export class RecordingStorageService {
  private db: IDBDatabase | null = null;

  /**
   * Initialize the database
   */
  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(new Error('Database initialization failed'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });

          // Create indexes for efficient querying
          store.createIndex('appointmentId', 'appointmentId', { unique: false });
          store.createIndex('uploadStatus', 'uploadStatus', { unique: false });
          store.createIndex('recordedAt', 'recordingMetadata.recordedAt', { unique: false });
        }
      };
    });
  }

  /**
   * Save a recording to IndexedDB
   */
  async saveRecording(recording: StoredRecording): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(recording);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to save recording:', request.error);
        reject(new Error('Failed to save recording'));
      };
    });
  }

  /**
   * Get a recording by ID
   */
  async getRecording(id: string): Promise<StoredRecording | null> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        console.error('Failed to get recording:', request.error);
        reject(new Error('Failed to retrieve recording'));
      };
    });
  }

  /**
   * Get all pending recordings
   */
  async getPendingRecordings(): Promise<StoredRecording[]> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('uploadStatus');
      const request = index.getAll('pending');

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        console.error('Failed to get pending recordings:', request.error);
        reject(new Error('Failed to retrieve pending recordings'));
      };
    });
  }

  /**
   * Update recording status
   */
  async updateRecordingStatus(
    id: string,
    status: StoredRecording['uploadStatus'],
    errorMessage?: string
  ): Promise<void> {
    const recording = await this.getRecording(id);
    if (!recording) {
      throw new Error('Recording not found');
    }

    recording.uploadStatus = status;
    recording.uploadAttempts += status === 'uploading' ? 1 : 0;
    recording.lastUploadAttempt = new Date();

    if (errorMessage) {
      recording.errorMessage = errorMessage;
    }

    await this.saveRecording(recording);
  }

  /**
   * Delete a recording
   */
  async deleteRecording(id: string): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to delete recording:', request.error);
        reject(new Error('Failed to delete recording'));
      };
    });
  }

  /**
   * Clean up old recordings (older than 24 hours)
   */
  async cleanupOldRecordings(): Promise<number> {
    if (!this.db) {
      await this.initialize();
    }

    const RETENTION_MS = 72 * 60 * 60 * 1000; // 72 hours

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.openCursor();
      let deletedCount = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const recording = cursor.value as StoredRecording;
          const recordedAt = recording.recordingMetadata?.recordedAt
            ? new Date(recording.recordingMetadata.recordedAt).getTime()
            : 0;
          const age = Date.now() - recordedAt;

          // Only clean up completed recordings older than 72 hours
          if (recording.uploadStatus === 'completed' && age > RETENTION_MS) {
            store.delete(cursor.primaryKey);
            deletedCount++;
          }
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };

      request.onerror = () => {
        console.error('Cleanup failed:', request.error);
        reject(new Error('Failed to cleanup old recordings'));
      };
    });
  }

  /**
   * Get all recordings regardless of status
   */
  async getAllRecordings(): Promise<StoredRecording[]> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        console.error('Failed to get all recordings:', request.error);
        reject(new Error('Failed to retrieve recordings'));
      };
    });
  }

  /**
   * Get storage size estimate
   */
  async getStorageInfo(): Promise<{
    usage: number;
    quota: number;
    percentUsed: number;
  }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;

      return {
        usage,
        quota,
        percentUsed: quota > 0 ? (usage / quota) * 100 : 0
      };
    }

    // Fallback for browsers without storage estimate
    return {
      usage: 0,
      quota: 0,
      percentUsed: 0
    };
  }
}

// Export singleton instance
export const recordingStorage = new RecordingStorageService();
